import React, { createContext, useState, useContext, useEffect, useCallback, ReactNode, useRef } from 'react';
import api from '../services/api';
import { Cookies } from 'react-cookie';
import { API_BASE_URL, COOKIE_DOMAIN, LOGIN_URL, LOGOUT_URL, BASE_URL } from '../config/env';
import { useSessionManager } from '../common/hooks/useSessionManager';
import { UserAppAccessResponse } from '../types/appAccess';
import { appChunkPreloader, PreloadSummary } from '../services/appChunkPreloader';

const cookies = new Cookies();


// Define types/interfaces
interface User {
  email?: string;
  first_name?: string;
  last_name?: string;
  is_staff?: boolean;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  appAccess: UserAppAccessResponse | null;
  loading: boolean;
  authError: string | null;
  showTimeoutWarning: boolean;
  preloadSummary: PreloadSummary | null;
  isPreloading: boolean;
  login: () => void;
  loginB2C: () => void;
  logout: () => Promise<void>;
  clearError: () => void;
  checkAuthStatus: () => Promise<void>;
  extendSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

// Auth check throttling
const AUTH_CHECK_THROTTLE = 10000; // 10 seconds between auth checks

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [appAccess, setAppAccess] = useState<UserAppAccessResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<string | null>(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [preloadSummary, setPreloadSummary] = useState<PreloadSummary | null>(null);
  const [isPreloading, setIsPreloading] = useState<boolean>(false);

  // Refs for throttling
  const originalTitle = useRef<string>(document.title);
  const originalFavicon = useRef<string>('');
  const authCheckInProgress = useRef<boolean>(false);
  const lastAuthCheck = useRef<number>(0);

  // Browser tab warning management
  const updateBrowserTabWarning = useCallback((showWarning: boolean) => {
    if (showWarning) {
      const newTitle = `Session expiring - ${originalTitle.current}`;
      
      // Only update if title actually changed
      if (document.title !== newTitle) {
        document.title = newTitle;
      }
      
      // Update favicon to warning icon
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.font = '24px Arial';
        ctx.fillStyle = '#ff0000';
        ctx.fillText('⚠️', 4, 24);
        
        const dataURL = canvas.toDataURL();
        let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.rel = 'icon';
          document.head.appendChild(favicon);
        }
        if (favicon.href !== dataURL) {
          favicon.href = dataURL;
        }
      }
    } else {
      // Only update if title actually changed
      if (document.title !== originalTitle.current) {
        document.title = originalTitle.current;
      }
      
      // Restore original favicon - more robust approach
      if (originalFavicon.current) {
        let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (favicon) {
          favicon.href = originalFavicon.current;
        }
      } else {
        // If we don't have the original favicon, try to find the default one
        const defaultFavicon = document.querySelector('link[rel="icon"][href*="favicon"]') as HTMLLinkElement;
        if (defaultFavicon) {
          let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
          if (favicon && favicon !== defaultFavicon) {
            favicon.href = defaultFavicon.href;
          }
        }
      }
    }
  }, []);

  // Simplified logout
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true);
    setAuthError(null);
    try {
      await api.post('/accounts/logout/');
    } catch (error: any) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setAppAccess(null);
      setShowTimeoutWarning(false);
      setPreloadSummary(null);
      setIsPreloading(false);
      
      // Clear preloader cache on logout
      appChunkPreloader.clearCache();
      
      cookies.remove('csrftoken', { domain: COOKIE_DOMAIN });
      cookies.remove('sessionid', { domain: COOKIE_DOMAIN });
      
      // Clean up any pending shared data on logout
      localStorage.removeItem('pendingSharedData');
      localStorage.removeItem('pendingSharedDataTimestamp');
      sessionStorage.removeItem('redirectAfterLogin');
      
      window.location.href = LOGOUT_URL;
    }
  }, []);

  // Session manager callbacks
  const sessionCallbacks = useCallback(() => ({
    onSessionExpired: () => {
      logout();
    },
    onShowWarning: () => {
      setShowTimeoutWarning(true);
      updateBrowserTabWarning(true);
    },
    onHideWarning: () => {
      setShowTimeoutWarning(false);
      updateBrowserTabWarning(false);
    },
    onSessionExtended: () => {
      // Session was extended successfully - ensure favicon is restored
      if (showTimeoutWarning) {
        setShowTimeoutWarning(false);
        updateBrowserTabWarning(false);
      }
    }
  }), [logout, updateBrowserTabWarning, showTimeoutWarning]);

  // Use the session manager hook
  const { extendSession } = useSessionManager(isAuthenticated, sessionCallbacks());

  // Preload authorized app chunks after authentication
  const preloadAuthorizedApps = useCallback(async (appAccessData: UserAppAccessResponse): Promise<void> => {
    if (!appAccessData?.accessible_apps || appAccessData.accessible_apps.length === 0) {
      return;
    }

    setIsPreloading(true);
    try {
      const summary = await appChunkPreloader.preloadAuthorizedApps(appAccessData.accessible_apps);
      setPreloadSummary(summary);
    } catch (error) {
      console.error('❌ Failed to preload app chunks:', error);
      setPreloadSummary(null);
    } finally {
      setIsPreloading(false);
    }
  }, []);

  // Optimized auth status check with better throttling
  const checkAuthStatus = useCallback(async (): Promise<void> => {
    if (authCheckInProgress.current) return;

    const now = Date.now();
    if (now - lastAuthCheck.current < AUTH_CHECK_THROTTLE) return;

    authCheckInProgress.current = true;
    lastAuthCheck.current = now;
    setLoading(true);
    setAuthError(null);

    try {
      const userResponse = await api.get('/users/me');
      if (userResponse.status === 200 && userResponse.data) {
        setUser(userResponse.data as User);
        setIsAuthenticated(true);

        try {
          // Use the new app-access endpoint for structured app data
          const appAccessResponse = await api.get('/users/app-access');
          if (appAccessResponse.status === 200 && appAccessResponse.data) {
            const appAccessData = appAccessResponse.data as UserAppAccessResponse;
            // Store the full app access data - this handles all permission logic
            setAppAccess(appAccessData);
            
            // Preload authorized app chunks in the background for better UX
            preloadAuthorizedApps(appAccessData).catch(error => {
              console.error('Background preloading failed:', error);
            });
          } else {
            setAppAccess(null);
          }
        } catch (permError) {
          console.error('Error fetching user app access:', permError);
          setAppAccess(null);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setAppAccess(null);
      }
    } catch (error: any) {
      console.error('Error checking auth status:', error);
      setUser(null);
      setIsAuthenticated(false);
      setAppAccess(null);
      setPreloadSummary(null);
      setIsPreloading(false);
    } finally {
      setLoading(false);
      authCheckInProgress.current = false;
    }
  }, [preloadAuthorizedApps]);

  // Store original title and favicon
  useEffect(() => {
    originalTitle.current = document.title;
    
    // Capture original favicon more robustly
    const favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
    if (favicon && favicon.href) {
      originalFavicon.current = favicon.href;
    } else {
      // Try to find any favicon in the document
      const allFavicons = document.querySelectorAll('link[rel*="icon"]');
      for (let i = 0; i < allFavicons.length; i++) {
        const iconElement = allFavicons[i] as HTMLLinkElement;
        if (iconElement.href && !iconElement.href.includes('data:')) {
          originalFavicon.current = iconElement.href;
          break;
        }
      }
    }
  }, []);

  // Initial auth check
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');

    if (code && state) {
      checkAuthStatus();
    } else {
      checkAuthStatus();
    }
  }, [checkAuthStatus]);

  // Simplified login functions
  const login = useCallback((): void => {
    setAuthError(null);
    setLoading(true);
    const returnUrl = encodeURIComponent(BASE_URL);
    const backendLoginUrl = `${API_BASE_URL}accounts/microsoft/login/?next=${returnUrl}`;
    window.location.href = backendLoginUrl;
  }, []);

  const loginB2C = useCallback((): void => {
    setAuthError(null);
    setLoading(true);
    const returnUrl = encodeURIComponent(BASE_URL);
    const backendB2CLoginUrl = `${API_BASE_URL}accounts/oidc/azure_b2c/login/?next=${returnUrl}`;
    window.location.href = backendB2CLoginUrl;
  }, []);

  // Response interceptor for auth redirects
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if ((error.response?.status === 401 || error.response?.status === 403) && 
            !window.location.pathname.includes(LOGIN_URL)) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          cookies.remove('csrftoken', { domain: COOKIE_DOMAIN });
          cookies.remove('sessionid', { domain: COOKIE_DOMAIN });
          window.location.href = LOGIN_URL;
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, []);

  const clearError = (): void => {
    setAuthError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        appAccess,
        loading,
        authError,
        showTimeoutWarning,
        preloadSummary,
        isPreloading,
        login,
        loginB2C,
        logout,
        clearError,
        checkAuthStatus,
        extendSession
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;